import axios from 'axios';
import { auth } from './firebase';

// Pour le web, on utilise localStorage directement
const storage = {
  getItem: (key) => Promise.resolve(localStorage.getItem(key)),
  setItem: (key, value) => Promise.resolve(localStorage.setItem(key, value)),
  removeItem: (key) => Promise.resolve(localStorage.removeItem(key))
};

// En dev (vite) : backend local sur 8080 (aligné .env.development backend).
// En prod (vite build) : nginx → /api/ proxié vers le backend.
// VITE_API_URL permet de surcharger.
export const API_URL = import.meta.env.VITE_API_URL
  || (import.meta.env.DEV ? 'http://localhost:8080' : '/api');

// Service de log simplifié pour le web
export const logService = {
  infoLog: (...args) => console.log('[INFO]', ...args),
  errorLog: (...args) => console.error('[ERROR]', ...args),
  warnLog: (...args) => console.warn('[WARN]', ...args)
};

/**
 * Construit l'URL d'un asset depuis le backend
 */
export const getAssetUrl = (assetPath) => {
  const cleanPath = assetPath.startsWith('/') ? assetPath.slice(1) : assetPath;
  return `${API_URL}/assets/${cleanPath}`;
};

// Création d'une instance axios avec la configuration de base
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Intercepteur pour les requêtes
axiosInstance.interceptors.request.use(
  async (config) => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      const token = await currentUser.getIdToken();
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      // Fallback sur localStorage si pas d'utilisateur Firebase
      const token = await storage.getItem('authToken');
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    console.error("Erreur lors de la préparation de la requête:", error);
    logService.errorLog("Erreur lors de la préparation de la requête:", error);
    return Promise.reject(error);
  }
);

// Intercepteur pour les réponses
axiosInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    if (error.response?.status === 401) {
      logService.infoLog("Token potentiellement expiré, tentative de renouvellement");
      
      try {
        const currentUser = auth.currentUser;
        if (currentUser) {
          const newToken = await currentUser.getIdToken(true);
          await storage.setItem('authToken', newToken);
          
          const originalRequest = error.config;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axiosInstance(originalRequest);
        } else {
          logService.errorLog("Erreur 401 : Utilisateur non connecté");
          await storage.removeItem('authToken');
          if (typeof window !== 'undefined') {
            window.location.href = '/login';
          }
          return Promise.reject(error);
        }
      } catch (refreshError) {
        logService.errorLog("Échec du renouvellement du token:", refreshError);
        await storage.removeItem('authToken');
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }
    
    const errorMessage = error.response?.data?.error || error.message;
    logService.errorLog("Erreur API:", errorMessage);

    const enhancedError = new Error(errorMessage);
    enhancedError.status = error.response?.status;
    enhancedError.data = error.response?.data;
    return Promise.reject(enhancedError);
  }
);

// Fonction utilitaire pour les requêtes API
export const apiRequest = async (method, endpoint, data = null, additionalConfig = {}) => {
  try {
    const config = {
      ...additionalConfig,
      headers: {
        ...additionalConfig.headers
      }
    };
    
    let controller;
    let timeoutId;
    
    if (additionalConfig.timeout) {
      controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), additionalConfig.timeout);
      config.signal = controller.signal;
    }

    console.log(method, endpoint, data, config);
    logService.infoLog(method, endpoint, data, config);
        
    const response = await axiosInstance({
      method,
      url: endpoint,
      data: method !== 'get' ? (data !== null && data !== undefined ? data : undefined) : null,
      params: method === 'get' ? (data?.params || data) : null,
      ...config
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    console.log("response : ", method, endpoint, ': ', response);
    logService.infoLog("response : ", method, endpoint, ': ', response);
    return response;
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error('La requête a dépassé le délai d\'attente');
      timeoutError.isTimeout = true;
      throw timeoutError;
    }

    console.error(`Erreur API Request (${endpoint}):`, error.message);
    throw error;
  }
};

// Fonction utilitaire pour les requêtes en streaming
export const apiRequestStream = async (method, endpoint, data = null) => {
  try {
    const currentUser = auth.currentUser;
    let token;
    if (currentUser) {
      token = await currentUser.getIdToken();
    } else {
      token = await storage.getItem('authToken');
    }
    
    const options = {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };

    if (data) {
      options.body = JSON.stringify(data);
    }

    const response = await fetch(`${API_URL}${endpoint}`, options);
    
    if (!response.ok) {
      const errorMessage = await response.text();
      throw new Error(errorMessage || `HTTP error! status: ${response.status}`);
    }

    return response;
  } catch (error) {
    console.error(`Erreur API Request Stream (${endpoint}):`, error.message);
    logService.errorLog(`Erreur API Request Stream (${endpoint}):`, error.message);
    throw error;
  }
};
